import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  MailOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import {
  notificationTimeout,
  notificationType,
} from '../common/constants';
import notValid from '../common/notValid';
import useAuthClaims from '../common/useAuthClaims';
import {
  firInviteAdmin,
  firListSiteStaff,
  firRemoveAdmin,
  firResendAdminInvite,
  firSetIsDebugEnabled,
  primaryAuth,
} from '../firebase';
import './style.scss';

// rev 86 status enum (aligned with project-wide requestStatus convention):
//   0 Pending, 1 Active, 2 Deactivated
const STATUS_PENDING     = 0;
const STATUS_ACTIVE      = 1;
const STATUS_DEACTIVATED = 2;

/** Format a Firestore Timestamp as "Invited 2 days ago" / "yesterday" / etc. */
function formatRelative(ts) {
  if (!ts) return '';
  // Firestore Timestamp from getDocs() exposes .toMillis(); older / raw
  // map shapes might come through as { seconds, nanoseconds }.
  let ms = null;
  if (typeof ts.toMillis === 'function') ms = ts.toMillis();
  else if (ts.seconds) ms = ts.seconds * 1000;
  if (!ms) return '';
  const diff = Date.now() - ms;
  const day = 24 * 60 * 60 * 1000;
  if (diff < 60 * 1000) return 'just now';
  if (diff < 60 * 60 * 1000) {
    const m = Math.round(diff / 60000);
    return `${m} min${m === 1 ? '' : 's'} ago`;
  }
  if (diff < day) {
    const h = Math.round(diff / 3600000);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  if (diff < 2 * day) return 'yesterday';
  if (diff < 30 * day) {
    const d = Math.round(diff / day);
    return `${d} days ago`;
  }
  if (diff < 365 * day) {
    const mo = Math.round(diff / (30 * day));
    return `${mo} month${mo === 1 ? '' : 's'} ago`;
  }
  const y = Math.round(diff / (365 * day));
  return `${y} year${y === 1 ? '' : 's'} ago`;
}

const { Text } = Typography;

// D24 rev 80 (2026-05-15) — Site Staff tab on SiteConfig.
//
// Visible only to users with the `superAdmin` claim (or `root` via the
// inherited cross-site capability). Lists the site's SuperAdmin + any
// Admins, and lets the SuperAdmin invite / remove Admins.
//
// "Remove" is a SOFT deactivate (accounts.status = 0 + admin claim
// cleared). The account doc and Auth user are kept for audit.
export default function SiteConfigSiteStaff({ siteId }) {
  const claims = useAuthClaims();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteForm] = Form.useForm();

  const [messageApi, contextHolder] = message.useMessage();
  const { timeoutError, timeoutSuccess } = notificationTimeout;
  const { typeError, typeSuccess } = notificationType;

  const openMessage = useCallback(
    (content, duration, type) => messageApi.open({ content, duration, type }),
    [messageApi],
  );

  const fetchStaff = useCallback(
    async (nameSite) => {
      if (notValid(nameSite)) return;
      setLoading(true);
      try {
        const rows = await firListSiteStaff(nameSite);
        setStaff(rows);
      } catch (e) {
        console.error('-- Error listing site staff:', e);
        openMessage(`Error listing site staff: ${e.message}`, timeoutError, typeError);
      } finally {
        setLoading(false);
      }
    },
    [openMessage, timeoutError, typeError],
  );

  useEffect(() => {
    if (claims.ready && (claims.superAdmin || claims.root)) {
      fetchStaff(siteId);
    }
  }, [claims.ready, claims.superAdmin, claims.root, fetchStaff, siteId]);

  const populateInviteForm = useCallback(() => {
    inviteForm.resetFields();
  }, [inviteForm]);

  const openInvite = () => setInviteOpen(true);

  const handleInviteOk = async () => {
    try {
      const values = await inviteForm.validateFields();
      setInviteSaving(true);
      const result = await firInviteAdmin(
        siteId,
        values.email.trim().toLowerCase(),
        values.fullname.trim(),
        (values.contactNumber || '').trim(),
      );
      const msg = {
        created: 'Admin invited. Invite email sent.',
        promoted: 'Existing user promoted to Admin. Invite email sent.',
        reactivated: 'Previously deactivated Admin reactivated. Invite email sent.',
        transferred: 'User transferred from another site. Invite email sent.',
      }[result.action] || 'Admin invited.';
      openMessage(msg, timeoutSuccess, typeSuccess);
      setInviteOpen(false);
      inviteForm.resetFields();
      fetchStaff(siteId);
    } catch (e) {
      if (e.errorFields) return;
      console.error('-- Error inviting admin:', e);
      openMessage(`Error: ${e.message}`, timeoutError, typeError);
    } finally {
      setInviteSaving(false);
    }
  };

  const handleRemove = async (row) => {
    try {
      await firRemoveAdmin(siteId, row.uid);
      const verb = row.status === STATUS_PENDING ? 'Invitation cancelled' : 'Deactivated';
      openMessage(`${verb}: ${row.fullname || row.email}`, timeoutSuccess, typeSuccess);
      fetchStaff(siteId);
    } catch (e) {
      console.error('-- Error removing admin:', e);
      openMessage(`Error: ${e.message}`, timeoutError, typeError);
    }
  };

  const handleResend = async (row) => {
    try {
      await firResendAdminInvite(siteId, row.uid);
      openMessage(
        `Invite resent to ${row.email}`,
        timeoutSuccess,
        typeSuccess,
      );
      fetchStaff(siteId);
    } catch (e) {
      console.error('-- Error resending invite:', e);
      openMessage(`Error: ${e.message}`, timeoutError, typeError);
    }
  };

  // Re-invite a Deactivated admin by reusing the existing row data —
  // no need for SuperAdmin to retype email / name / contact. v2inviteAdmin
  // handles status=0 -> status=3 via its `reactivated` branch.
  const handleReinvite = async (row) => {
    try {
      const result = await firInviteAdmin(
        siteId,
        row.email,
        row.fullname || row.email,
        row.contactNumber || '',
      );
      const msg = result.action === 'reactivated'
        ? `Re-invited ${row.email}`
        : `Invite sent to ${row.email}`;
      openMessage(msg, timeoutSuccess, typeSuccess);
      fetchStaff(siteId);
    } catch (e) {
      console.error('-- Error re-inviting:', e);
      openMessage(`Error: ${e.message}`, timeoutError, typeError);
    }
  };

  // rev 107 — toggle the rev 81 mobile staff-gate escape hatch for
  // a specific Admin. Used by QA / engineering for end-to-end testing
  // on the resident app without permanently demoting the account.
  const handleToggleDebug = async (row, nextValue) => {
    try {
      const byUid = primaryAuth.currentUser?.uid;
      if (!byUid) {
        openMessage('Sign-in expired — refresh and try again', timeoutError, typeError);
        return;
      }
      await firSetIsDebugEnabled(row.uid, nextValue, byUid);
      openMessage(
        nextValue
          ? `Debug enabled for ${row.fullname || row.email}`
          : `Debug disabled for ${row.fullname || row.email}`,
        timeoutSuccess,
        typeSuccess,
      );
      fetchStaff(siteId);
    } catch (e) {
      console.error('-- Error toggling debug:', e);
      openMessage(`Error: ${e.message}`, timeoutError, typeError);
    }
  };

  // ---------- Render ----------

  if (!claims.ready) return <Text type="secondary">Loading…</Text>;

  if (!claims.superAdmin && !claims.root) {
    return (
      <Alert
        message="SuperAdmin only"
        description="Only the site's SuperAdmin can manage Site Staff."
        showIcon
        type="warning"
      />
    );
  }

  const columns = [
    {
      title: 'Role',
      dataIndex: 'accessLevel',
      width: 120,
      render: (val, row) => {
        if (val === 11) return <Tag color="purple">SuperAdmin</Tag>;
        if (val === 12) return <Tag color="blue">Admin</Tag>;
        return <Tag>{row.accountType || `lvl ${val}`}</Tag>;
      },
      filters: [
        { text: 'SuperAdmin', value: 11 },
        { text: 'Admin', value: 12 },
      ],
      onFilter: (v, r) => r.accessLevel === v,
    },
    {
      title: 'Name',
      dataIndex: 'fullname',
      render: (val, row) => (
        <Space size={8} wrap>
          <span>{val || <Text type="secondary">—</Text>}</span>
          {row.uid === claims.uid && (
            <Tag
              color="gold"
              bordered={false}
              style={{
                fontWeight: 600,
                letterSpacing: '0.3px',
                margin: 0,
                lineHeight: '20px',
              }}
            >
              You
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      render: (val) => val || <Text type="secondary">—</Text>,
    },
    {
      title: 'Contact',
      dataIndex: 'contactNumber',
      width: 140,
      render: (val) => val || <Text type="secondary">—</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 180,
      filters: [
        { text: 'Active', value: STATUS_ACTIVE },
        { text: 'Pending', value: STATUS_PENDING },
        { text: 'Deactivated', value: STATUS_DEACTIVATED },
      ],
      onFilter: (v, r) => r.status === v,
      render: (val, row) => {
        if (val === STATUS_PENDING) {
          return (
            <Space direction="vertical" size={0}>
              <Tag color="orange">Pending invite</Tag>
              {row.invitedAt && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Invited {formatRelative(row.invitedAt)}
                </Text>
              )}
            </Space>
          );
        }
        if (val === STATUS_DEACTIVATED) {
          return <Tag color="default">Deactivated</Tag>;
        }
        return (
          <Space direction="vertical" size={0}>
            <Tag color="success">Active</Tag>
            {row.acceptedAt && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Joined {formatRelative(row.acceptedAt)}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Debug',
      key: 'debug',
      width: 90,
      render: (_, row) => {
        // rev 107 — only meaningful on Active Admin rows (level 12).
        // SuperAdmin (11) is managed elsewhere; Pending/Deactivated rows
        // can't sign in to the resident app anyway.
        if (row.accessLevel !== 12 || row.status !== STATUS_ACTIVE) {
          return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
        }
        return (
          <Switch
            checked={row.isDebugEnabled}
            onChange={(next) => handleToggleDebug(row, next)}
            size="small"
            title="Allow this account past the mobile staff-redirect gate (rev 81). For QA / engineering only."
          />
        );
      },
    },
    {
      title: 'Actions',
      width: 240,
      render: (_, row) => {
        // SuperAdmin row — managed by Manage Sites tab, not here.
        if (row.accessLevel === 11) {
          return <Text type="secondary" style={{ fontSize: 12 }}>Managed by ATACT staff</Text>;
        }
        // Deactivated — one-click Re-invite (calls v2inviteAdmin's
        // `reactivated` branch). Uses the row's existing email/name/
        // contact so SuperAdmin doesn't have to re-type.
        if (row.status === STATUS_DEACTIVATED) {
          return (
            <Popconfirm
              cancelText="Cancel"
              okText="Re-invite"
              onConfirm={() => handleReinvite(row)}
              title={`Re-invite ${row.fullname || row.email}?`}
              description={
                <span style={{ display: 'block', maxWidth: 280 }}>
                  Sends a fresh invite email to <strong>{row.email}</strong>.
                  Their account moves back to <strong>Pending</strong> until they sign in.
                </span>
              }
            >
              <Button
                icon={<UserAddOutlined />}
                size="small"
                type="link"
              >
                Re-invite
              </Button>
            </Popconfirm>
          );
        }
        // Pending — Resend Invite + Cancel.
        if (row.status === STATUS_PENDING) {
          return (
            <Space size={4} wrap>
              <Button
                icon={<MailOutlined />}
                onClick={() => handleResend(row)}
                size="small"
                type="link"
              >
                Resend
              </Button>
              <Popconfirm
                cancelText="Keep"
                okText="Cancel invite"
                okType="danger"
                onConfirm={() => handleRemove(row)}
                title={`Cancel invite for ${row.fullname || row.email}?`}
                description={
                  <span style={{ display: 'block', maxWidth: 280 }}>
                    Cancels the invitation. They keep their accounts record
                    in case you want to re-invite later.
                  </span>
                }
              >
                <Button danger icon={<DeleteOutlined />} size="small" type="link">
                  Cancel
                </Button>
              </Popconfirm>
            </Space>
          );
        }
        // Active — Deactivate (soft).
        return (
          <Popconfirm
            cancelText="Cancel"
            okText="Deactivate"
            okType="danger"
            onConfirm={() => handleRemove(row)}
            title={`Deactivate ${row.fullname || row.email}?`}
            description={
              <span style={{ display: 'block', maxWidth: 280 }}>
                Soft deactivate — their account is kept for audit but they
                lose admin access. You can re-invite the same email later.
              </span>
            }
          >
            <Button danger icon={<DeleteOutlined />} size="small" type="link">
              Deactivate
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <>
      {contextHolder}
      <Alert
        message="Manage your site's admins"
        description={
          <span>
            The SuperAdmin (you) is listed for reference and can only be
            reassigned by ATACT staff. Invite additional Admins to share
            review duties on account applications and per-site config.
            Removing an Admin <strong>soft-deactivates</strong> their
            account — they keep their Firestore record for audit but lose
            admin access.
          </span>
        }
        showIcon
        style={{ marginBottom: 16 }}
        type="info"
      />
      <Flex justify="flex-end" style={{ marginBottom: 12 }}>
        <Button icon={<UserAddOutlined />} onClick={openInvite} type="primary">
          Invite Admin
        </Button>
      </Flex>
      <Table
        columns={columns}
        dataSource={staff}
        loading={loading}
        pagination={false}
        rowKey="uid"
        rowClassName={(row) => (row.uid === claims.uid ? 'is-self-row' : '')}
      />

      <Modal
        title="Invite Admin"
        open={inviteOpen}
        onOk={handleInviteOk}
        confirmLoading={inviteSaving}
        onCancel={() => {
          setInviteOpen(false);
          inviteForm.resetFields();
        }}
        okText="Send Invite"
        afterOpenChange={(open) => { if (open) populateInviteForm(); }}
        destroyOnClose
        width={480}
      >
        <Alert
          message="What this does"
          description={
            <ul style={{ margin: '4px 0 0 0', paddingLeft: 18, fontSize: 13 }}>
              <li>Finds or creates a Firebase Auth user with this email.</li>
              <li>Grants <span className="mono">admin</span> claim (accessLevel 12).</li>
              <li>Adds them to <span className="mono">accounts</span> with{' '}
                  <span className="mono">assignedSite</span> = this site.</li>
              <li>Sends a password-setup email; after they set a password
                  they&apos;re sent to{' '}
                  <span className="mono">atactconnect.web.app/signIn</span>.</li>
            </ul>
          }
          showIcon
          style={{ marginBottom: 16 }}
          type="info"
        />
        <Form form={inviteForm} layout="vertical" preserve={false}>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please enter an email' },
              { type: 'email', message: 'Looks malformed' },
            ]}
            tooltip="The address that will receive the password-setup invite."
          >
            <Input placeholder="e.g., bob@example.com" maxLength={120} />
          </Form.Item>
          <Form.Item
            label="Full Name"
            name="fullname"
            rules={[{ required: true, message: 'Please enter a full name' }]}
          >
            <Input placeholder="e.g., Bob Lee" maxLength={80} />
          </Form.Item>
          <Form.Item
            label="Contact Number (optional)"
            name="contactNumber"
          >
            <Input placeholder="e.g., +65 9123 4567" maxLength={30} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

SiteConfigSiteStaff.propTypes = {
  siteId: PropTypes.string.isRequired,
};
